import { translate } from "@/plugins/i18n";
import type BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";
import { Maybe } from "@/components/utils/typeHelpers";


export class EnumCurrency implements BaseEnumType {
  private static allValues: EnumCurrency[] = []; // make sure this is top-most
  
  public static none = new EnumCurrency(0, "None", "enum_currency_none");
  public static gBP = new EnumCurrency(1, "GBP", "enum_currency_g_b_p");
  public static eUR = new EnumCurrency(2, "EUR", "enum_currency_e_u_r");
  public static kES = new EnumCurrency(3, "KES", "enum_currency_k_e_s");
  public static uSD = new EnumCurrency(4, "USD", "enum_currency_u_s_d");
  public static aUD = new EnumCurrency(5, "AUD", "enum_currency_a_u_d");
  public static nZD = new EnumCurrency(6, "NZD", "enum_currency_n_z_d");
  public static sEK = new EnumCurrency(7, "SEK", "enum_currency_s_e_k");
  public static zAR = new EnumCurrency(8, "ZAR", "enum_currency_z_a_r");
  public static cOP = new EnumCurrency(9, "COP", "enum_currency_c_o_p");
  public static tHB = new EnumCurrency(10, "THB", "enum_currency_t_h_b");
  public static pHP = new EnumCurrency(11, "PHP", "enum_currency_p_h_p");
  public static xCD = new EnumCurrency(12, "XCD", "enum_currency_x_c_d");
  public static dKK = new EnumCurrency(13, "DKK", "enum_currency_d_k_k");
  public static cAD = new EnumCurrency(14, "CAD", "enum_currency_c_a_d");
  public static cHF = new EnumCurrency(15, "CHF", "enum_currency_c_h_f");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumCurrency.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isNone() { return this === EnumCurrency.none; }
  public isGBP() { return this === EnumCurrency.gBP; }
  public isEUR() { return this === EnumCurrency.eUR; }
  public isKES() { return this === EnumCurrency.kES; }
  public isUSD() { return this === EnumCurrency.uSD; }
  public isAUD() { return this === EnumCurrency.aUD; }
  public isNZD() { return this === EnumCurrency.nZD; }
  public isSEK() { return this === EnumCurrency.sEK; }
  public isZAR() { return this === EnumCurrency.zAR; }
  public isCOP() { return this === EnumCurrency.cOP; }
  public isTHB() { return this === EnumCurrency.tHB; }
  public isPHP() { return this === EnumCurrency.pHP; }
  public isXCD() { return this === EnumCurrency.xCD; }
  public isDKK() { return this === EnumCurrency.dKK; }
  public isCAD() { return this === EnumCurrency.cAD; }
  public isCHF() { return this === EnumCurrency.cHF; }

  public static getValues(): readonly EnumCurrency[] {
    return EnumCurrency.allValues;
  }

  public static getValuesExcept0(): readonly EnumCurrency[] {
    return EnumCurrency.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumCurrency {
    const found = EnumCurrency.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumCurrency - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumCurrency): EnumCurrency {
    const found = name ? EnumCurrency.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumCurrency - Value not found: ${ name }`);
    }

    return found;
  }

  public equals(other: Maybe<EnumCurrency>): boolean {
    return this.key === other?.key;
  }
}